/**
 * The validity form (1.J stage 3, deliverable 26), one component for the Story panel and the
 * Inspector: "When this link holds", a start and an end said in 1.O-1's When input, and "Ongoing".
 * Save sends one `links.update` on the link's source path with only what changed; Cancel and Esc
 * send nothing. A refused date is a warning beside its side and the typed text stays.
 */
import type { LinkBoundaryV1, LinkV1 } from '@codex/contracts';
import { ApiError } from '@codex/contracts';
import { Button, Checkbox, hasMod, Icon, useLayer, useReturnFocus } from '@codex/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useNotify } from '../../shell/notices.js';
import { useRoutePaths } from '../../shell/sidebar-types.js';
import { useWhenContext } from '../../when/calendar-context.js';
import { WhenInput, type WhenInputState } from '../../when/when-input.js';
import { isSavable, whenToBoundary } from '../../when/when-model.js';
import { CALENDAR_FAILED, NO_CALENDAR } from '../../when/words.js';
import { useLinksApi } from '../api.js';
import { endSessionOn401 } from '../failure.js';
import { ROW_CONFLICT_KEPT_DATES_TEXT } from '../messages.js';
import {
  type BoundaryRefusal,
  boundaryRefusal,
  sideText,
  validityPatch,
} from './validity-model.js';

type Side = 'from' | 'to';

const SIDE_WORDS = {
  from: { label: 'Starts', change: 'Change start', clear: 'Clear start' },
  to: { label: 'Ends', change: 'Change end', clear: 'Clear end' },
} as const;

const NO_TITLES: ReadonlyMap<string, string> = new Map();

export interface ValidityFormProps {
  readonly link: LinkV1;
  /** The page the form is shown on. */
  readonly docId: string;
  /** Where focus goes when the form closes. */
  readonly returnTo: HTMLElement | null;
  readonly onClose: () => void;
}

interface Typed {
  /** The boundary the typed text means, when it means one. */
  readonly boundary: LinkBoundaryV1 | null;
  /** Why it means none, when the read is valid and the boundary still cannot be used. */
  readonly reason: string | null;
  readonly ready: boolean;
}

export function ValidityForm({ link, docId, returnTo, onClose }: ValidityFormProps) {
  const api = useLinksApi();
  const client = useQueryClient();
  const notify = useNotify();
  const paths = useRoutePaths();
  const ctx = useWhenContext();
  const form = useRef<HTMLFormElement>(null);
  const ongoingGroup = useId();
  const [draft, setDraft] = useState({
    validFrom: link.validFrom,
    validTo: link.validTo,
    ongoing: link.ongoing,
  });
  const [editing, setEditing] = useState<Side | null>(null);
  const [typed, setTyped] = useState<Typed | null>(null);
  const [refusal, setRefusal] = useState<BoundaryRefusal | null>(null);
  const [saving, setSaving] = useState(false);
  useLayer(true, onClose, 'form');
  useReturnFocus(true, returnTo);

  const focused = useRef(false);
  const ready = ctx.status === 'ready';
  useEffect(() => {
    if (!ready || focused.current) return;
    focused.current = true;
    form.current?.querySelector<HTMLElement>('[data-validity-change]')?.focus();
  }, [ready]);

  // The refusal is tied to its side's input (the When input owns the element).
  useEffect(() => {
    const input = form.current?.querySelector<HTMLElement>('[data-testid="when.input.field"]');
    if (!input) return;
    const target = form.current?.querySelector('[data-validity-message]')?.id;
    if (target) input.setAttribute('aria-describedby', target);
    else input.removeAttribute('aria-describedby');
  });

  const calendar = ctx.status === 'ready' ? ctx.calendar : null;

  const onRead = useCallback(
    (state: WhenInputState) => {
      if (calendar === null || state.read === null) return;
      const result = whenToBoundary(state.read, { ...state.choice, calendar });
      setRefusal(null);
      if (result.ok) setTyped({ boundary: result.boundary, reason: null, ready: state.ready });
      else
        setTyped({
          boundary: null,
          reason: isSavable(state.read) ? result.reason : null,
          ready: false,
        });
    },
    [calendar],
  );

  // The draft as it would be sent: the typed side replaces its boundary.
  const effective = (() => {
    if (editing === null || typed === null || typed.boundary === null || !typed.ready) return draft;
    return editing === 'from'
      ? { ...draft, validFrom: typed.boundary }
      : { ...draft, validTo: typed.boundary };
  })();
  const blocked = editing !== null && (typed === null || !typed.ready);

  const patch = validityPatch(link, effective);

  const save = async () => {
    if (blocked || saving) return;
    if (patch === null) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      await api.update(link.sourceId, link.id, { baseRv: link.rv, ...patch });
      await Promise.all([
        client.invalidateQueries({ queryKey: ['links'] }),
        client.invalidateQueries({ queryKey: ['when', 'links'] }),
      ]);
      onClose();
    } catch (error) {
      setSaving(false);
      if (endSessionOn401(error)) return;
      const shown = boundaryRefusal(error);
      if (error instanceof ApiError && error.code === 'row_conflict') {
        notify(ROW_CONFLICT_KEPT_DATES_TEXT);
        await client.invalidateQueries({ queryKey: ['links'] });
      } else if (shown.side === null) notify(shown.text);
      else setRefusal(shown);
    }
  };

  const reasonShown = editing !== null ? typed?.reason : null;
  const messageFor = (side: Side): string | null => {
    if (editing === side && reasonShown) return reasonShown;
    if (refusal?.side === side) return refusal.text;
    return null;
  };

  const sideView = (side: Side) => {
    const words = SIDE_WORDS[side];
    const boundary = side === 'from' ? effective.validFrom : effective.validTo;
    const message = messageFor(side);
    const shownLink: LinkV1 = {
      ...link,
      validFrom: effective.validFrom,
      validTo: effective.validTo,
    };
    return (
      <div className="cx-links-validity__side" data-side={side}>
        {editing === side ? (
          calendar !== null && (
            <WhenInput
              docId={link.sourceId}
              label={words.label}
              allow={['point', 'now', 'relative', 'undated', 'session']}
              autoFocus
              onRead={onRead}
              onSubmit={() => void save()}
            />
          )
        ) : (
          <>
            <span className="cx-links-validity__label">{words.label}</span>
            <span className="cx-links-validity__words">
              {calendar === null ? '' : sideText(shownLink, side, docId, NO_TITLES, calendar)}
            </span>
            <Button
              size="sm"
              variant="ghost"
              type="button"
              data-validity-change=""
              onClick={() => {
                setEditing(side);
                setTyped(null);
                setRefusal(null);
              }}
            >
              {words.change}
            </Button>
          </>
        )}
        {boundary !== null && editing !== side && (
          <Button
            size="sm"
            variant="ghost"
            type="button"
            onClick={() => {
              setDraft({ ...effective, [side === 'from' ? 'validFrom' : 'validTo']: null });
              setEditing(null);
              setRefusal(null);
            }}
          >
            {words.clear}
          </Button>
        )}
        {message && (
          <p
            role="alert"
            id={`${ongoingGroup}-${side}`}
            data-validity-message=""
            className="cx-links-validity__message"
          >
            <Icon name="warning" size={14} /> {message}
          </p>
        )}
      </div>
    );
  };

  return (
    <form
      ref={form}
      className="cx-links-validity"
      aria-label="Link dates"
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
      onKeyDown={(event) => {
        if (event.defaultPrevented) return;
        if (event.key === 'Enter' && hasMod(event)) {
          event.preventDefault();
          void save();
        }
      }}
    >
      <fieldset className="cx-links-validity__set">
        <legend>When this link holds</legend>
        {ctx.status !== 'ready' ? (
          <p className="cx-links-story__empty" role="status">
            {ctx.status === 'loading'
              ? 'Loading the calendar…'
              : ctx.status === 'error'
                ? CALENDAR_FAILED
                : NO_CALENDAR}{' '}
            {ctx.status === 'none' && paths.includes('/calendar') && (
              <a href="/calendar">Set up the calendar</a>
            )}
          </p>
        ) : (
          <>
            {sideView('from')}
            {!effective.ongoing && sideView('to')}
            <Checkbox
              label="Ongoing (no end yet)"
              checked={effective.ongoing}
              onChange={(ongoing) => {
                if (ongoing && editing === 'to') setEditing(null);
                setDraft({ ...effective, ongoing, validTo: ongoing ? null : effective.validTo });
              }}
            />
          </>
        )}
      </fieldset>
      <div className="cx-links-note-form__actions">
        <Button variant="primary" size="sm" type="submit" disabled={blocked || saving}>
          Save
        </Button>
        <Button variant="ghost" size="sm" type="button" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
